










































































































































































































































































import { Component, Vue } from 'vue-property-decorator';
import {
    DiagnosticBusyError, DiagnosticEventRow, DiagnosticNotConfiguredError, DiagnosticRunPayload, DiagnosticRunRow,
    fetchDiagnosticConfig, fetchDiagnosticEvents,
    fetchDiagnosticRuns, finishDiagnosticRun, startDiagnosticRun,
} from '@/api/diagnostic';
import TrendChart from '@/components/Diagnostic/TrendChart.vue';
import { MeasureState, onMeasureChange } from '@/utils/diagnostic/continuous';
import {
    DEFAULT_SECONDS, DiagnosticCancelled, DiagnosticError, DiagnosticStep, MAX_SECONDS, MIN_SECONDS, runDiagnostic,
} from '@/utils/diagnostic/runner';
import {
    BAD, buildSeries, estimateMos, FlowState, latestMediaIp, Level, levelOf, Sample, Series, summarize, Summary,
    Verdict, WARN,
} from '@/utils/diagnostic/summary';

type ErrorKind = 'notConfigured' | 'config' | 'userBusy' | 'domainBusy' | 'serviceBusy' | 'register' | 'call';

// Cada cuanto se refresca la tabla del dia: asi se ven las pruebas de los demas pasar a "En curso" y terminar.
const RUNS_REFRESH_MS = 15000;
type Phase = 'config' | DiagnosticStep;

const PHASES: Phase[] = ['config', 'register', 'call', 'measure'];

const PHASE_LABEL: Record<Phase, string> = {
    config: 'Preparando la prueba',
    register: 'Conectando con la central',
    call: 'Haciendo una llamada de prueba',
    measure: 'Midiendo la calidad del audio',
};

const ERROR_PHASE: Record<ErrorKind, Phase> = {
    notConfigured: 'config',
    config: 'config',
    userBusy: 'config',
    domainBusy: 'config',
    serviceBusy: 'config',
    register: 'register',
    call: 'call',
};

const VERDICT_TEXT: Record<Verdict, { title: string; box: string }> = {
    ok: { title: '✅ Tu conexión está bien para llamadas', box: 'bg-green-50 text-green-800' },
    warn: { title: '⚠️ Podrías notar cortes', box: 'bg-yellow-50 text-yellow-800' },
    bad: { title: '❌ Tu conexión no es apta para llamadas', box: 'bg-red-50 text-red-800' },
    noAudio: { title: '❌ No llegó el audio', box: 'bg-red-50 text-red-800' },
};

// segundos que se promedian para el estado actual
const HEALTH_WINDOW = 10;
const LEVEL_LABEL: Record<Level, string> = { ok: 'Bien', warn: 'Regular', bad: 'Mal' };
const LEVEL_CLASS: Record<Level, string> = { ok: 'text-green-700', warn: 'text-yellow-700', bad: 'text-red-700' };

const STATE_LABEL: Record<FlowState, string> = {
    waiting: 'conectando',
    ok: 'audio fluyendo',
    cut: 'sin audio',
};
// Colores en linea (misma paleta de la app): la hoja de estilos de dev se arma al arrancar y no
// incluye las clases nuevas hasta reiniciar, y un grafico sin color no se entiende.
const COLOR = {
    waiting: '#D1D5DB',
    full: '#10B981',
    low: '#FBBF24',
    cut: '#EF4444',
    empty: '#F3F4F6',
};

const LEGEND = [
    { label: 'Conectando', color: COLOR.waiting },
    { label: 'Audio completo', color: COLOR.full },
    { label: 'Llegó menos audio', color: COLOR.low },
    { label: 'No llegó audio', color: COLOR.cut },
];

const CHART_HEIGHT_PX = 96;

// Una barra por debajo de esta parte de la más alta se pinta de amarillo.
const LOW_SHARE = 0.8;
// Alto mínimo para que el segundo "chato" se vea como barra y no desaparezca.
const MIN_BAR_PCT = 8;

const RUN_RESULT: Record<string, { label: string; cls: string }> = {
    running: { label: 'En curso', cls: 'bg-blue-100 text-blue-800' },
    abandoned: { label: 'Sin cierre', cls: 'bg-gray-100 text-gray-600' },
    ok: { label: 'Bien', cls: 'bg-green-100 text-green-800' },
    warn: { label: 'Regular', cls: 'bg-yellow-100 text-yellow-800' },
    bad: { label: 'Mal', cls: 'bg-red-100 text-red-800' },
    noAudio: { label: 'Sin audio', cls: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-700' },
    failed: { label: 'No conectó', cls: 'bg-red-100 text-red-800' },
};

const ERROR_MESSAGE: Record<ErrorKind, { title: string; detail: string }> = {
    notConfigured: {
        title: 'La prueba no está disponible por ahora',
        detail: 'No está configurada en este entorno. No tiene que ver con tu conexión.',
    },
    config: {
        title: 'No pudimos preparar la prueba',
        detail: 'No se pudo obtener la configuración de la prueba desde el servidor.',
    },
    userBusy: {
        title: 'Ya tenés una prueba en curso',
        detail: 'Hay otra prueba tuya corriendo, por ejemplo en otra pestaña. Se puede hacer una a la vez.',
    },
    domainBusy: {
        title: 'Hay otras pruebas en curso en tu compañía',
        detail: 'Para no sobrecargar la central se limita cuántas pruebas pueden correr a la vez. '
            + 'Cuando termine alguna se puede volver a probar.',
    },
    serviceBusy: {
        title: 'Hay muchas pruebas en curso en este momento',
        detail: 'Para no afectar el funcionamiento del panel se limita cuántas pruebas pueden correr '
            + 'a la vez. Cuando termine alguna se puede volver a probar.',
    },
    register: {
        title: 'No pudimos conectar con la central',
        detail: 'Posibles causas: no hay conexión a internet, la red bloquea la conexión con la central '
            + 'o la central no respondió.',
    },
    call: {
        title: 'No pudimos completar la llamada de prueba',
        detail: 'Se logró conectar con la central, pero la llamada se cortó antes de terminar. '
            + 'Posibles causas: la red bloquea el audio o la conexión se interrumpió.',
    },
};

function errorKind(error: unknown): ErrorKind {
    if (error instanceof DiagnosticNotConfiguredError) {
        return 'notConfigured';
    }
    if (error instanceof DiagnosticBusyError) {
        // user_busy -> userBusy, domain_busy -> domainBusy, service_busy -> serviceBusy
        const [scope] = error.reason.split('_');

        return `${scope}Busy` as ErrorKind;
    }
    if (error instanceof DiagnosticError) {
        return error.kind;
    }
    return 'config';
}

function connectionInfo(): string {
    const { connection } = navigator as any;
    if (!connection) {
        return 'no informada por el navegador';
    }
    const parts = [
        connection.effectiveType,
        connection.downlink ? `${connection.downlink} Mbps` : '',
        connection.rtt ? `RTT ${connection.rtt} ms` : '',
    ];

    return parts.filter(Boolean).join(', ');
}

async function copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const area = document.createElement('textarea');
    area.value = text;
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
}

@Component({ components: { TrendChart } })
export default class Diagnostic extends Vue {
    running = false;

    started = false;

    phase: Phase | null = null;

    failedPhase: Phase | null = null;

    summary: Summary | null = null;

    samples: Sample[] = [];

    error: ErrorKind | null = null;

    errorDetail = '';

    startedAt = 0;

    finishedAt = 0;

    domain = '';

    seconds = DEFAULT_SECONDS;

    total = DEFAULT_SECONDS;

    cancelled = false;

    stopRequested = false;

    copied = false;

    minSeconds = MIN_SECONDS;

    maxSeconds = MAX_SECONDS;

    limits = WARN;

    badLimits = BAD;

    runs: DiagnosticRunRow[] = [];

    runsError = false;

    runId: number | null = null;

    clientIp: string | null = null;

    refreshTimer = 0;

    auto: MeasureState = {
        status: 'idle',
        seconds: 0,
        rtt: null,
        lossPct: 0,
        jitterMs: 0,
        events: 0,
        lastEvent: null,
        history: { rtt: [], loss: [], jitter: [] },
    };

    stopAuto: () => void = () => undefined;

    get autoTitle(): string {
        const titles: Record<string, string> = {
            idle: 'La medición continua está detenida.',
            otherTab: 'Se está midiendo desde otra pestaña del panel.',
            connecting: 'Conectando para medir tu conexión…',
            measuring: 'Midiendo tu conexión constantemente',
            paused: 'Medición en pausa: la pestaña está en segundo plano.',
            retrying: 'Sin conexión con la central. Reintentando…',
            full: 'Ya hay 4 personas de tu compañía midiendo. Se vuelve a intentar en unos minutos.',
            unavailable: 'La medición no está disponible por ahora.',
        };

        return titles[this.auto.status];
    }

    /** Estado de la conexion ahora: promedio de los ultimos segundos para que no parpadee. */
    get autoHealth(): { level: Level; word: string; mos: number } | null {
        const { history } = this.auto;
        const mean = (values: number[]) => {
            const last = values.slice(-HEALTH_WINDOW);

            return last.length ? last.reduce((sum, value) => sum + value, 0) / last.length : null;
        };
        const loss = mean(history.loss);
        const jitter = mean(history.jitter);
        const rtt = mean(history.rtt);

        if (loss === null || jitter === null || rtt === null) {
            return null;
        }

        const rank: Record<Level, number> = { ok: 0, warn: 1, bad: 2 };
        const level = [levelOf(loss, 'lossPct'), levelOf(jitter, 'jitterMs'), levelOf(rtt, 'rttMs')]
            .reduce<Level>((worst, item) => (rank[item] > rank[worst] ? item : worst), 'ok');
        const words: Record<Level, string> = { ok: 'bien', warn: 'regular', bad: 'mal' };

        return { level, word: words[level], mos: estimateMos(rtt, jitter, loss) };
    }

    get measuredFor(): string {
        const { seconds } = this.auto;

        return seconds >= 120 ? `${Math.round(seconds / 60)} min` : `${seconds} s`;
    }

    get autoBox(): string {
        if (this.auto.status === 'measuring' && this.autoHealth) {
            const boxes: Record<Level, string> = {
                ok: 'border-green-200 bg-green-50',
                warn: 'border-yellow-200 bg-yellow-50',
                bad: 'border-red-200 bg-red-50',
            };

            return boxes[this.autoHealth.level];
        }

        return this.auto.status === 'retrying' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white';
    }

    get autoDot(): string {
        const dots: Record<string, string> = {
            connecting: 'bg-blue-300 animate-pulse', retrying: 'bg-red-500', full: 'bg-yellow-500',
        };
        const health: Record<Level, string> = { ok: 'bg-green-500', warn: 'bg-yellow-500', bad: 'bg-red-500' };

        if (this.auto.status === 'measuring') {
            return this.autoHealth ? health[this.autoHealth.level] : 'bg-blue-400 animate-pulse';
        }

        return dots[this.auto.status] || 'bg-gray-400';
    }

    // eslint-disable-next-line class-methods-use-this
    eventLabel(kind: string): string {
        const labels: Record<string, string> = {
            cut: 'corte de audio',
            loss: 'pérdida de paquetes',
            jitter: 'jitter alto',
            latency: 'latencia alta',
            dropped: 'llamada caída',
            // eslint-disable-next-line @typescript-eslint/camelcase
            register_failed: 'la central no respondió',
        };

        return labels[kind] || kind;
    }

    // eslint-disable-next-line class-methods-use-this
    eventPeak(event: DiagnosticEventRow): string {
        const units: Record<string, string> = { loss: ' %', jitter: ' ms', latency: ' ms' };

        return event.peak === null ? '—' : `${Math.round(event.peak * 10) / 10}${units[event.kind] || ''}`;
    }

    events: DiagnosticEventRow[] = [];

    created() {
        this.stopAuto = onMeasureChange((value) => { this.auto = value; });
        this.loadRuns();
        // Con la pestaña oculta nadie mira la tabla: no se consulta.
        this.refreshTimer = window.setInterval(() => {
            if (!document.hidden) {
                this.loadRuns();
            }
        }, RUNS_REFRESH_MS);
    }

    beforeDestroy() {
        window.clearInterval(this.refreshTimer);
        this.stopAuto();
    }

    async loadRuns() {
        try {
            this.runs = await fetchDiagnosticRuns();
            this.runsError = false;
            this.events = await fetchDiagnosticEvents().catch(() => this.events);
        } catch (error) {
            this.runsError = true;
        }
    }

    get runsSummary(): string {
        const count = (key: string) => this.runs.filter((run) => this.resultKey(run) === key).length;
        const good = count('ok');
        const fair = count('warn');
        const bad = this.runs.filter((run) => ['bad', 'noAudio', 'failed'].includes(this.resultKey(run))).length;
        const live = count('running');
        const summary = `${this.runs.length} pruebas hoy · ${good} bien · ${fair} regular · ${bad} mal o sin conexión`;

        return live ? `${summary} · ${live} en curso` : summary;
    }

    // eslint-disable-next-line class-methods-use-this
    resultKey(run: DiagnosticRunRow): string {
        return run.status === 'completed' ? run.verdict || 'ok' : run.status;
    }

    resultOf(run: DiagnosticRunRow) {
        return RUN_RESULT[this.resultKey(run)] || RUN_RESULT.failed;
    }

    // eslint-disable-next-line class-methods-use-this
    timeOf(run: DiagnosticRunRow): string {
        return new Date(run.started_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    }

    // eslint-disable-next-line class-methods-use-this
    format(value: number | null, unit: string, digits = 0): string {
        return value === null || value === undefined ? '—' : `${value.toFixed(digits)}${unit}`;
    }

    get series(): Series {
        return buildSeries(this.samples);
    }

    get secondsValid(): boolean {
        return Number.isInteger(this.seconds) && this.seconds >= MIN_SECONDS && this.seconds <= MAX_SECONDS;
    }

    get progress(): number {
        return Math.round((this.samples.length / this.total) * 100);
    }

    get steps() {
        const current = this.phase ? PHASES.indexOf(this.phase) : PHASES.length;
        const failed = this.failedPhase ? PHASES.indexOf(this.failedPhase) : -1;

        return PHASES.map((phase, index) => {
            let state = 'pending';
            if (failed === index) {
                state = 'error';
            } else if (failed !== -1 ? index < failed : index < current) {
                state = 'done';
            } else if (this.running && index === current) {
                state = 'active';
            }

            return {
                phase,
                label: PHASE_LABEL[phase],
                ...this.stateStyle(state),
            };
        });
    }

    /** La IP con la que la central recibe el audio: aparece apenas el par ICE se elige. */
    get mediaIp(): string | null {
        return (this.summary && this.summary.mediaIp) || latestMediaIp(this.samples);
    }

    get shown(): Summary | null {
        if (this.summary) {
            return this.summary;
        }
        return this.samples.length ? summarize(this.samples) : null;
    }

    get cards() {
        const { shown } = this;
        if (!shown) {
            return [];
        }
        const rtt = shown.rttMs;
        const metrics: Array<{
            label: string; value: string; level: Level; reference: string; noData?: boolean;
        }> = [
            {
                label: 'Pérdida',
                value: `${shown.lossPct.toFixed(1)} %`,
                level: levelOf(shown.lossPct, 'lossPct'),
                reference: `menos de ${WARN.lossPct} %`,
            },
            {
                label: 'Jitter',
                value: `${Math.round(shown.jitterMs)} ms`,
                level: levelOf(shown.jitterMs, 'jitterMs'),
                reference: `menos de ${WARN.jitterMs} ms`,
            },
            {
                label: 'Latencia',
                value: rtt === null ? '—' : `${Math.round(rtt)} ms`,
                level: levelOf(rtt, 'rttMs'),
                reference: `menos de ${WARN.rttMs} ms`,
                noData: rtt === null,
            },
        ];

        return metrics.map((metric) => ({
            ...metric,
            levelLabel: metric.noData ? 'Sin dato' : LEVEL_LABEL[metric.level],
            levelClass: metric.noData ? 'text-gray-500' : LEVEL_CLASS[metric.level],
        }));
    }

    get verdictText() {
        return VERDICT_TEXT[(this.summary as Summary).verdict];
    }

    /** Que se midio y que problema podria estar pasando: describe, no recomienda. */
    get findings(): string[] {
        const summary = this.summary as Summary;
        if (summary.verdict === 'noAudio') {
            return ['La llamada se conectó, pero no llegó audio: la conexión no deja pasar el audio de las llamadas.'];
        }

        const found: string[] = [];
        if (levelOf(summary.lossPct, 'lossPct') !== 'ok') {
            found.push(`Se perdió el ${summary.lossPct.toFixed(1)} % del audio. `
                + 'Puede notarse como cortes o voz metálica.');
        }
        if (levelOf(summary.jitterMs, 'jitterMs') !== 'ok') {
            found.push(`El audio llegó de forma irregular (jitter de ${Math.round(summary.jitterMs)} ms). `
                + 'Puede notarse como voz entrecortada o a destiempo.');
        }
        if (summary.rttMs !== null && levelOf(summary.rttMs, 'rttMs') !== 'ok') {
            found.push(`La demora de ida y vuelta fue de ${Math.round(summary.rttMs)} ms. `
                + 'Puede notarse como desfase al hablar.');
        }
        if (summary.cutSeconds > 0) {
            const unit = summary.cutSeconds === 1 ? 'segundo' : 'segundos';
            found.push(`Hubo ${summary.cutSeconds} ${unit} sin audio. Puede notarse como silencios o cortes.`);
        }

        return found.length ? found : ['Pérdida, jitter y latencia están dentro de lo esperado.'];
    }

    get errorMessage() {
        return this.error ? ERROR_MESSAGE[this.error] : null;
    }

    // eslint-disable-next-line class-methods-use-this
    stateStyle(state: string) {
        const styles: Record<string, { icon: string; iconClass: string; textClass: string }> = {
            done: { icon: '✓', iconClass: 'bg-green-100 text-green-700', textClass: 'text-gray-700' },
            active: { icon: '…', iconClass: 'bg-blue-100 text-blue-700 animate-pulse', textClass: 'font-semibold' },
            error: { icon: '✕', iconClass: 'bg-red-100 text-red-700', textClass: 'text-red-700 font-semibold' },
            pending: { icon: '', iconClass: 'bg-gray-100', textClass: 'text-gray-400' },
        };

        return styles[state];
    }

    legend = LEGEND;

    /** El segundo con más audio: las demás barras se miden contra él. */
    get peak(): number {
        const { received, states } = this.series;
        return Math.max(0, ...received.filter((_, index) => states[index] === 'ok'));
    }

    /** Un casillero por segundo de la prueba, medidos o no. */
    get slots(): number[] {
        return Array.from({ length: Math.max(this.total, this.samples.length) }, (_, index) => index);
    }

    chartStyle = {
        display: 'flex',
        alignItems: 'flex-end',
        height: `${CHART_HEIGHT_PX}px`,
        borderBottom: '1px solid #E5E7EB',
    };

    barStyle(index: number): Record<string, string> {
        const base = {
            flex: '1 1 0',
            minWidth: '0',
            margin: '0 1px',
            borderRadius: '2px 2px 0 0',
        };
        const state = this.series.states[index];

        if (state === undefined) {
            // todavia no se midio: casillero vacio, para que se vea cuanto falta
            return { ...base, height: '100%', backgroundColor: COLOR.empty };
        }
        if (state !== 'ok') {
            return {
                ...base,
                height: `${MIN_BAR_PCT}%`,
                backgroundColor: state === 'cut' ? COLOR.cut : COLOR.waiting,
            };
        }

        const share = this.peak > 0 ? this.series.received[index] / this.peak : 1;

        return {
            ...base,
            height: `${Math.max(MIN_BAR_PCT, Math.round(share * 100))}%`,
            backgroundColor: share < LOW_SHARE ? COLOR.low : COLOR.full,
        };
    }

    barTitle(index: number): string {
        const state = this.series.states[index];

        return state === undefined
            ? `Segundo ${index + 1}: todavía sin medir`
            : `Segundo ${index + 1}: ${STATE_LABEL[state]}`;
    }

    // eslint-disable-next-line class-methods-use-this
    stateLabel(state: FlowState): string {
        return STATE_LABEL[state];
    }

    resultText(): string {
        const shown = this.shown as Summary;
        const lines = [
            `Diagnóstico de conexión - ${new Date(this.finishedAt).toLocaleString('es-AR')}`,
            `Resultado: ${this.verdictText.title}`,
            `Central: ${this.domain}`,
            `IP pública (la que ve la central): ${this.mediaIp || 'sin dato'}`,
            `IP vista por el panel: ${this.clientIp || 'sin dato'}`,
            `Duración de la prueba: ${this.total} s`,
            `Pérdida: ${shown.lossPct.toFixed(1)} % (ideal: menos de ${WARN.lossPct} %)`,
            `Jitter: ${Math.round(shown.jitterMs)} ms (ideal: menos de ${WARN.jitterMs} ms)`,
            `Latencia: ${shown.rttMs === null ? 'sin dato' : `${Math.round(shown.rttMs)} ms`}`
                + ` (ideal: menos de ${WARN.rttMs} ms)`,
            `Segundos sin audio: ${shown.cutSeconds} de ${this.total}`,
            `Red: ${connectionInfo()}`,
            `Navegador: ${navigator.userAgent}`,
            'Observaciones:',
            ...this.findings.map((finding) => `- ${finding}`),
        ];

        return lines.join('\n');
    }

    async copyResult() {
        await copyToClipboard(this.resultText());
        this.copied = true;
        window.setTimeout(() => { this.copied = false; }, 4000);
    }

    cancel() {
        this.stopRequested = true;
    }

    /* eslint-disable @typescript-eslint/camelcase */
    // Las claves son el contrato snake_case del backend.
    saveFailure(error: Error) {
        this.saveRun('failed', {
            failed_step: this.failedPhase || undefined,
            error_detail: this.errorDetail || error.message,
        });
    }

    /** Cierra la corrida con lo medido hasta ahora; si falla el guardado no se le muestra nada al usuario. */
    saveRun(status: DiagnosticRunPayload['status'], extra: Partial<DiagnosticRunPayload> = {}) {
        const { connection } = navigator as any;
        const measured = this.summary || (this.samples.length ? summarize(this.samples) : null);

        if (this.runId === null) {
            return;
        }

        finishDiagnosticRun(this.runId, {
            samples: this.samples.length,
            status,
            verdict: status === 'completed' && measured ? measured.verdict : undefined,
            loss_pct: measured ? Number(measured.lossPct.toFixed(2)) : undefined,
            jitter_ms: measured ? Math.round(measured.jitterMs) : undefined,
            rtt_ms: measured && measured.rttMs !== null ? Math.round(measured.rttMs) : undefined,
            cut_seconds: measured ? measured.cutSeconds : undefined,
            media_ip: measured && measured.mediaIp ? measured.mediaIp : undefined,
            effective_type: connection ? connection.effectiveType : undefined,
            downlink: connection ? connection.downlink : undefined,
            user_agent: navigator.userAgent,
            ...extra,
        })
            .then(() => this.loadRuns())
            .catch((error) => console.warn('No se pudo guardar la prueba:', error));
    }
    /* eslint-enable @typescript-eslint/camelcase */

    async start() {
        if (!this.secondsValid) {
            return;
        }
        this.total = this.seconds;
        this.stopRequested = false;
        this.cancelled = false;
        this.copied = false;
        this.running = true;
        this.started = true;
        this.summary = null;
        this.samples = [];
        this.error = null;
        this.errorDetail = '';
        this.failedPhase = null;
        this.phase = 'config';
        this.runId = null;
        this.clientIp = null;
        this.startedAt = Date.now();

        try {
            const config = await fetchDiagnosticConfig();
            this.domain = config.domain;
            // Sin cupo no se registra ni se llama: el servidor limita las pruebas simultaneas.
            const slot = await startDiagnosticRun(this.total);
            this.runId = slot.id;
            this.clientIp = slot.clientIp;
            this.loadRuns();
            this.summary = await runDiagnostic(config, this.total, {
                onStep: (step) => { this.phase = step; },
                isCancelled: () => this.stopRequested,
                onSamples: (samples) => { this.samples = samples; },
            });
            this.finishedAt = Date.now();
            this.saveRun('completed');
        } catch (error) {
            if (error instanceof DiagnosticCancelled) {
                this.cancelled = true;
                this.saveRun('cancelled');
                return;
            }
            this.error = errorKind(error);
            // La fase la mueven los callbacks de runDiagnostic: TS no lo ve y la dejaria fija en 'config'.
            const reached = this.phase as Phase | null;
            this.failedPhase = reached === 'measure' ? 'measure' : ERROR_PHASE[this.error];
            this.errorDetail = error instanceof DiagnosticError ? error.detail : '';
            // Sin cupo o sin configuracion no hay corrida que cerrar.
            this.saveFailure(error as Error);
        } finally {
            this.running = false;
            this.phase = null;
        }
    }
}
